import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { EventInput } from '@/graphql-types/globalTypes';

interface MemberLearningContext_EventQuery {
  event: {
    eventV2:
      | { __typename: 'SimpleEventV2'; isCourseWorkshop: boolean }
      | { __typename: 'ConferenceEventV2' }
      | null;
  };
}

interface MemberLearningContext_EventQueryVariables {
  input: EventInput;
}

const MEMBER_LEARNING_CONTEXT_ON_EVENT_ROUTE = gql`
  query MemberLearningContextOnEventRoute_EventQuery($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        __typename
        ... on SimpleEventV2 {
          isCourseWorkshop
        }
      }
    }
  }
`;

/**
 * True when the member is on a simple-event URL under `/events/:eventId/...`
 * (not conferences) and that event is a course/workshop — same “Learning”
 * context as `/learning` in the Coffee sidebar and top nav.
 */
export const useMemberLearningContextOnSimpleEventRoute = (): boolean => {
  const router = useRouter();
  const path = router.asPath.split('?')[0] ?? '';
  const eventId =
    typeof router.query.eventId === 'string' ? router.query.eventId : undefined;

  const isSimpleEventDetailBranch = React.useMemo(() => {
    if (!eventId || path.startsWith('/events/conferences')) {
      return false;
    }
    return path.startsWith(`/events/${eventId}`);
  }, [eventId, path]);

  const queryResult = useQuery<
    MemberLearningContext_EventQuery,
    MemberLearningContext_EventQueryVariables
  >(MEMBER_LEARNING_CONTEXT_ON_EVENT_ROUTE, {
    variables: { input: { _id: eventId ?? '' } },
    skip: !isSimpleEventDetailBranch || !eventId,
  });

  const eventV2 = queryResult.data?.event.eventV2;

  if (!isSimpleEventDetailBranch || !eventId) {
    return false;
  }

  return (
    eventV2?.__typename === 'SimpleEventV2' &&
    eventV2.isCourseWorkshop === true
  );
};
